import { useCallback, useRef, useState } from 'react';
import { type Decimal, formatAmount } from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';
import type { InvoiceLine } from '../../../../graphql/generated/graphql';

interface UseNumericInputsArgs {
  /** Called on blur with the raw string the user entered. Hosts should parse
   * and persist the value via their own update logic. */
  onCommit: (lineIndex: number, field: keyof InvoiceLine, raw: string) => void;
}

/** In-progress raw text per field for a single line. */
export type LineNumericBuffer = Readonly<Partial<Record<keyof InvoiceLine, string>>>;

interface NumericInputs {
  /** Buffered raw text for a line; `undefined` while the line is untouched.
   * Referentially stable per line — editing one line doesn't change the
   * buffers of other lines, so memoized rows skip re-rendering. */
  getLineBuffer: (lineIndex: number) => LineNumericBuffer | undefined;
  /** Stable read of a line's buffer as of the latest `handleChange`, before re-render. */
  peekLineBuffer: (lineIndex: number) => LineNumericBuffer | undefined;
  handleChange: (lineIndex: number, field: keyof InvoiceLine, raw: string) => void;
  /** Commit the buffered raw value via `onCommit` (if any) and clear the buffer. */
  handleBlur: (lineIndex: number, field: keyof InvoiceLine) => void;
  /** Drop all buffered raw values for a line (use when the line is deleted). */
  clearForLine: (lineIndex: number) => void;
  /** Drop the buffered raw value for a single (line, field). Used by "apply
   * expected value" affordances so the input re-renders from the committed
   * value rather than the stale typed buffer. */
  clearForField: (lineIndex: number, field: keyof InvoiceLine) => void;
}

/** String shown in a numeric input: the in-progress raw text if the user has
 * typed (and not yet blurred), otherwise the committed value via
 * `formatAmount`. Empty string when the stored value is null/undefined.
 * Inputs deliberately skip grouping and use 'en-US' so the value round-trips
 * through `parseDecimal` (which is locale-tolerant but doesn't strip dot
 * groupings). */
export function numericDisplayValue(
  buffer: LineNumericBuffer | undefined,
  field: keyof InvoiceLine,
  stored: Decimal | null | undefined,
  currencyCode?: CurrencyCode
): string {
  const raw = buffer?.[field];
  if (raw !== undefined) return raw;
  return stored == null
    ? ''
    : formatAmount(stored, { currencyCode, locale: 'en-US', useGrouping: false });
}

type Buffers = Record<number, Partial<Record<keyof InvoiceLine, string>>>;

/** Remove one field's buffer; drops the whole line entry when it empties.
 * Returns `prev` untouched when there is nothing to remove. */
function removeField(prev: Buffers, lineIndex: number, field: keyof InvoiceLine): Buffers {
  if (prev[lineIndex]?.[field] === undefined) return prev;
  const line = { ...prev[lineIndex] };
  delete line[field];
  const next = { ...prev };
  if (Object.keys(line).length === 0) {
    delete next[lineIndex];
  } else {
    next[lineIndex] = line;
  }
  return next;
}

/**
 * Buffers in-progress text for numeric inputs so a user can type "1," or "1."
 * without it being parsed and reformatted mid-keystroke. On blur the buffered
 * raw string is handed to `onCommit` and the buffer is cleared, after which
 * `numericDisplayValue` falls through to the committed value.
 */
export function useNumericInputs({ onCommit }: UseNumericInputsArgs): NumericInputs {
  const [buffers, setBuffers] = useState<Buffers>({});
  // Mirror for synchronous reads from the stable handlers below. All writes
  // happen in event handlers (sequential), so value-based updates are safe.
  const buffersRef = useRef(buffers);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const applyBuffers = useCallback((update: (prev: Buffers) => Buffers) => {
    const next = update(buffersRef.current);
    if (next === buffersRef.current) return;
    buffersRef.current = next;
    setBuffers(next);
  }, []);

  const getLineBuffer: NumericInputs['getLineBuffer'] = lineIndex => buffers[lineIndex];

  const peekLineBuffer: NumericInputs['peekLineBuffer'] = useCallback(
    lineIndex => buffersRef.current[lineIndex],
    []
  );

  const handleChange: NumericInputs['handleChange'] = useCallback(
    (lineIndex, field, raw) => {
      applyBuffers(prev => ({
        ...prev,
        [lineIndex]: { ...prev[lineIndex], [field]: raw }
      }));
    },
    [applyBuffers]
  );

  const handleBlur: NumericInputs['handleBlur'] = useCallback(
    (lineIndex, field) => {
      const raw = buffersRef.current[lineIndex]?.[field];
      if (raw === undefined) return;
      onCommitRef.current(lineIndex, field, raw);
      applyBuffers(prev => removeField(prev, lineIndex, field));
    },
    [applyBuffers]
  );

  const clearForLine: NumericInputs['clearForLine'] = useCallback(
    lineIndex => {
      applyBuffers(prev => {
        if (!(lineIndex in prev)) return prev;
        const next = { ...prev };
        delete next[lineIndex];
        return next;
      });
    },
    [applyBuffers]
  );

  const clearForField: NumericInputs['clearForField'] = useCallback(
    (lineIndex, field) => {
      applyBuffers(prev => removeField(prev, lineIndex, field));
    },
    [applyBuffers]
  );

  return { getLineBuffer, peekLineBuffer, handleChange, handleBlur, clearForLine, clearForField };
}
